import { Component } from '@theme/component';
import { preloadImage } from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Image-first viewer with a swipeable thumbnail strip.
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} media
 * @property {HTMLElement} thumbnails
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #highResImagesLoaded = new Set();
  #activeIndex = 0;
  #opener;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('image-zoom-change', this.#handleZoomChange);
    this.addEventListener('image-swipe', this.#handleSwipe);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('image-zoom-change', this.#handleZoomChange);
    this.removeEventListener('image-swipe', this.#handleSwipe);
  }

  #handleZoomChange = (event) => {
    if (this.refs.media[this.#activeIndex]?.contains(event.target)) this.#updateZoomButton(event.detail.zoomed);
  };

  #handleSwipe = (event) => {
    if (!this.refs.dialog.open || !this.refs.media[this.#activeIndex]?.contains(event.target)) return;
    this.selectThumbnail((this.#activeIndex + event.detail.step + this.refs.media.length) % this.refs.media.length);
  };

  open(index, event) {
    event.preventDefault();
    this.#opener = event.target instanceof Element ? event.target.closest('slideshow-slide, li') : document.activeElement;
    if (this.#opener instanceof HTMLElement && !this.#opener.hasAttribute('tabindex')) this.#opener.tabIndex = -1;
    this.refs.dialog.showModal();
    this.selectThumbnail(index);
    this.refs.dialog.scrollTop = 0;
  }

  /** @param {HTMLElement} mediaContainer */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }


  close() {
    this.refs.media[this.#activeIndex]?.querySelector('drag-zoom-wrapper')?.resetZoom();
    this.refs.dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
    if (this.#opener instanceof HTMLElement && this.#opener.isConnected) this.#opener.focus();
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      // Keep native video and model keyboard controls intact.
      if (event.target.closest('video, iframe, model-viewer, input, select, textarea')) return;
      event.preventDefault();
      const step = event.key === 'ArrowRight' ? 1 : -1;
      this.selectThumbnail((this.#activeIndex + step + this.refs.media.length) % this.refs.media.length);
    }
  }

  handleThumbnailClick(index) {
    this.selectThumbnail(index);
    this.refs.dialog.scrollTop = 0;
  }

  handleThumbnailPointerEnter(index) {
    if (this.refs.media[index]) this.#loadHighResolutionImage(this.refs.media[index]);
  }

  selectThumbnail(index) {
    const { media, thumbnails, dialog } = this.refs;
    if (!Number.isInteger(index) || !media[index]) return;
    media[this.#activeIndex]?.querySelector('drag-zoom-wrapper')?.resetZoom();
    // Stop playback when switching away from a video or model.
    if (index !== this.#activeIndex) {
      media[this.#activeIndex]?.querySelector('deferred-media')?.pauseMedia();
      media[this.#activeIndex]?.querySelector('product-model')?.pauseMedia();
    }
    this.#activeIndex = index;
    media.forEach((item, i) => { item.hidden = i !== index; });
    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.removeAttribute('aria-selected');
      button.setAttribute('aria-pressed', String(i === index));
    });
    const selectedThumbnail = thumbnails.querySelectorAll('button')[index];
    if (selectedThumbnail) {
      const left = thumbnails.scrollLeft + selectedThumbnail.getBoundingClientRect().left - thumbnails.getBoundingClientRect().left;
      thumbnails.scrollTo({ left: left - (thumbnails.clientWidth - selectedThumbnail.offsetWidth) / 2, behavior: 'instant' });
    }
    dialog.querySelector('[data-image-counter]').textContent = `${index + 1} / ${media.length}`;
    const zoom = dialog.querySelector('[data-image-zoom]');
    zoom.hidden = !media[index].classList.contains('product-media-container--image');
    this.#updateZoomButton(false);
    this.#loadHighResolutionImage(media[index]);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  toggleZoom() {
    const wrapper = this.refs.media[this.#activeIndex]?.querySelector('drag-zoom-wrapper');
    if (wrapper) this.#updateZoomButton(wrapper.toggleZoom());
  }

  #updateZoomButton(zoomed) {
    const button = this.refs.dialog.querySelector('[data-image-zoom]');
    const label = zoomed ? button.dataset.zoomOutLabel : button.dataset.zoomInLabel;
    button.setAttribute('aria-pressed', String(zoomed));
    button.setAttribute('aria-label', label);
    button.querySelector('[data-zoom-label]').textContent = label;
  }
}

if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);
